import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'

import Table from 'LuxKit/Tables/Table'
import TableBody from 'LuxKit/Tables/TableBody'
import TableBodyCell from 'LuxKit/Tables/TableBodyCell'
import TableBodyRow from 'LuxKit/Tables/TableBodyRow'
import TableHeader from 'LuxKit/Tables/TableHeader'
import TableHeaderCell from 'LuxKit/Tables/TableHeaderCell'
import TableHeaderRow from 'LuxKit/Tables/TableHeaderRow'

import Group from 'components/utils/Group'
import FormatCurrency from 'components/Common/FormatCurrency'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import config from 'constants/config'

const TableBodyRowTotal = styled(TableBodyRow)`
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
`

interface Props {
  oldPrice: number
  newPrice: number
  oldPropertyFees: number
  newPropertyFees: number
  className?: string
}

function PriceChangeViewV2(props: Props) {
  const {
    oldPrice,
    newPrice,
    oldPropertyFees,
    newPropertyFees,
    className,
  } = props

  return (
    <Group direction="vertical" gap={16} className={className}>
      <Table>
        <TableHeader>
          <TableHeaderRow>
            <TableHeaderCell minWidth={96}></TableHeaderCell>
            <TableHeaderCell minWidth={96}>
              <BodyText variant="medium" colour="neutral-three">
                <FormattedMessage
                  id="k9j3w8520e0wkUBIOj5U"
                  defaultMessage="Before"
                  description="Column header label for the previous price in the price breakdown"
                />
              </BodyText>
            </TableHeaderCell>
            <TableHeaderCell minWidth={96}>
              <BodyText variant="medium">
                <FormattedMessage
                  id="UVBN_3-xfCI4ab79mH_C"
                  defaultMessage="Now"
                  description="Column header label for the current price in the price breakdown"
                />
              </BodyText>
            </TableHeaderCell>
          </TableHeaderRow>
        </TableHeader>
        <TableBody>
          {oldPropertyFees !== newPropertyFees && (
            <>
              <TableBodyRow>
                <TableBodyCell>
                  <BodyText variant="medium">
                    <FormattedMessage
                      id="Za-a_uAqeuZoNezGAmgu"
                      defaultMessage="Pay Now"
                      description="Row label for the amount to pay now in the price change view section"
                    />
                  </BodyText>
                </TableBodyCell>
                <TableBodyCell>
                  <BodyText variant="medium" colour="neutral-three" strikeThrough={newPrice !== oldPrice}>
                    <FormatCurrency value={oldPrice} />
                  </BodyText>
                </TableBodyCell>
                <TableBodyCell>
                  <BodyText variant="medium">
                    <FormatCurrency value={newPrice} />
                  </BodyText>
                </TableBodyCell>
              </TableBodyRow>
              <TableBodyRow>
                <TableBodyCell>
                  <BodyText variant="medium">
                    <FormattedMessage
                      id="9WVzFoBCdOPQqGx6SMmf"
                      defaultMessage="Pay at hotel"
                      description="Row label for amount to pay at the hotel on the hotel page"
                    />
                  </BodyText>
                  <BodyText variant="small">
                    <FormattedMessage
                      id="rOFTi3nL-HRuxNysvwvq"
                      defaultMessage="Local Tax"
                      description="Sub-label indicating the fee is a local tax in the price change view section"
                    />
                  </BodyText>
                </TableBodyCell>
                <TableBodyCell>
                  <BodyText variant="medium" colour="neutral-three" strikeThrough>
                    <FormatCurrency value={oldPropertyFees} />
                  </BodyText>
                </TableBodyCell>
                <TableBodyCell>
                  <BodyText variant="medium">
                    <FormatCurrency value={newPropertyFees} />
                  </BodyText>
                </TableBodyCell>
              </TableBodyRow>
            </>
          )}
          <TableBodyRowTotal>
            <TableBodyCell>
              <BodyText variant="medium" weight="bold">
                <FormattedMessage
                  id="4Gh3z4jpBjYnx0OkmnWC"
                  defaultMessage="Total"
                  description="Row label for the total price amount in the checkout or order price breakdown view"
                />
              </BodyText>
            </TableBodyCell>
            <TableBodyCell>
              <BodyText variant="medium" weight="bold" colour="neutral-three" strikeThrough>
                <FormatCurrency value={oldPrice + oldPropertyFees} />
              </BodyText>
            </TableBodyCell>
            <TableBodyCell>
              <BodyText variant="medium" weight="bold">
                <FormatCurrency value={newPrice + newPropertyFees} />
              </BodyText>
            </TableBodyCell>
          </TableBodyRowTotal>
        </TableBody>
      </Table>
      {oldPropertyFees !== newPropertyFees && <BodyTextBlock variant="medium">
        <FormattedMessage
          id="PE0FsIIZsxgp10Jc5hKF"
          defaultMessage="Property fees are estimated by {configTitle} based on information provided by the property and are subject to change."
          description="Disclaimer about property fees being estimates in the price change view section"
          values={{ configTitle: config.title }}
        />
      </BodyTextBlock>}
    </Group>
  )
}

export default PriceChangeViewV2
